import { useCatalog } from '@/context/CatalogContext';
import { API_BASE_URL, API_IS_REMOTE, USE_MOCKS } from '@/config';
import styles from './Layout.module.css';

/**
 * Surfaces a failed catalog load.
 *
 * Without this the store renders normally but completely empty, which looks
 * like "there are no books" rather than "the backend is unreachable" — the two
 * failures are indistinguishable to anyone looking at the page.
 *
 * The advice splits on where the API lives. A local backend has usually just
 * not been started; a deployed one is almost always up and reachable (curl
 * proves it) while the browser still sees nothing, because CORS stripped the
 * response or the URL was built wrong. Offering the local checklist for a
 * remote failure sends people to look at the one thing that isn't broken.
 */
export function CatalogStatus() {
  const { status, error } = useCatalog();

  if (status !== 'error') return null;

  return (
    <div className={styles.catalogError} role="alert">
      <strong>Couldn&apos;t load the catalog.</strong>{' '}
      {USE_MOCKS ? (
        <>Bundled sample data failed to load — this is a bug, not a config problem.</>
      ) : (
        <>
          No books came back from <code>{API_BASE_URL}</code>. Check that:
          {API_IS_REMOTE ? (
            <ul className={styles.catalogErrorList}>
              <li>
                the API allows this page&apos;s origin — <code>{window.location.origin}</code>{' '}
                must appear in <code>CORS_ORIGINS</code> in the backend&apos;s{' '}
                <code>.env</code>. This is the usual cause: the request succeeds and the
                browser discards the response for want of an{' '}
                <code>Access-Control-Allow-Origin</code> header. The browser console says so
                explicitly; the network tab shows a <em>200</em>.
              </li>
              <li>
                the API is actually serving —{' '}
                <code>curl {API_BASE_URL}/api/products?limit=1</code>. If that returns books,
                the backend is fine and the problem is between it and this page.
              </li>
              <li>
                <code>VITE_API_BASE_URL</code> has no trailing slash and no stray whitespace.
                Paths are appended directly, so <code>…com/</code> builds{' '}
                <code>//api/products</code>, which the API answers with 404.
              </li>
              <li>
                the build picked the value up. Vite inlines it at{' '}
                <strong>build</strong> time, so a deployed bundle keeps whatever was set when
                it was built — changing the variable needs a rebuild, not a redeploy.
              </li>
            </ul>
          ) : (
            <ul className={styles.catalogErrorList}>
              <li>
                the backend is running on that exact port —{' '}
                <code>cd backend &amp;&amp; python main.py</code>
              </li>
              <li>
                <code>VITE_API_BASE_URL</code> in <code>.env.local</code> matches it. Vite reads
                that file only at startup, so restart the dev server after changing it.
              </li>
              <li>
                it uses <code>127.0.0.1</code>, not <code>localhost</code> — the latter resolves
                to IPv6 <code>::1</code> first, which the backend does not listen on, so the
                browser&apos;s connection is refused even while <code>curl</code> succeeds.
              </li>
            </ul>
          )}
        </>
      )}
      {error?.message && <div className={styles.catalogErrorDetail}>{error.message}</div>}
    </div>
  );
}
